/**
 * Public Signal Draft Generator V1 - Claim Extractor
 * 
 * Extracts safe claims from closeout artifacts for public drafts.
 * 
 * BOUNDARIES:
 * - Advisory only: no execution authority
 * - Deterministic output only
 * - No mutation of inputs
 */

import {
  CloseoutArtifact,
  SafeClaim,
} from './types';
import { convertToPublicLanguage } from './languageConverter';
import { isTextSafeForPublic } from './forbiddenTermScanner';

/**
 * Extract safe claims from issue field
 */
function extractClaimsFromIssue(issue: string): SafeClaim[] {
  const claims: SafeClaim[] = [];
  const convertedIssue = convertToPublicLanguage(issue);

  // Split into sentences
  const sentences = convertedIssue.split(/[.!?]+/).filter(s => s.trim().length > 0);

  for (const sentence of sentences) {
    if (isTextSafeForPublic(sentence)) {
      claims.push({
        claim: sentence.trim(),
        source: 'issue',
        confidence: 'high',
      });
    }
  }

  return claims;
}

/**
 * Extract safe claims from changes
 */
function extractClaimsFromChanges(changes: Array<{ type: string; description: string }>): SafeClaim[] {
  const claims: SafeClaim[] = [];

  for (const change of changes) {
    if (change.description) {
      const convertedDesc = convertToPublicLanguage(change.description);
      
      if (isTextSafeForPublic(convertedDesc)) {
        claims.push({
          claim: convertedDesc,
          source: 'changes',
          confidence: 'high',
        });
      }
    }
  }

  return claims;
}

/**
 * Extract safe claims from invariants
 */
function extractClaimsFromInvariants(invariants: string[]): SafeClaim[] {
  const claims: SafeClaim[] = [];

  for (const invariant of invariants) {
    const convertedInvariant = convertToPublicLanguage(invariant);
    
    if (isTextSafeForPublic(convertedInvariant)) {
      claims.push({
        claim: convertedInvariant,
        source: 'invariants',
        confidence: 'high',
      });
    }
  }

  return claims;
}

/**
 * Extract safe claims from test results
 */
function extractClaimsFromTests(testResults: { total: number; passed: number; failed: number }): SafeClaim[] {
  const claims: SafeClaim[] = [];

  if (testResults.total > 0 && testResults.passed === testResults.total) {
    claims.push({
      claim: `All ${testResults.total} verification checks passed.`,
      source: 'tests',
      confidence: 'high',
    });
  }

  if (testResults.passed > 0) {
    claims.push({
      claim: `${testResults.passed} of ${testResults.total} checks passed.`,
      source: 'tests',
      confidence: 'high',
    });
  }

  return claims;
}

/**
 * Extract all safe claims from closeout artifact
 */
export function extractSafeClaims(artifact: CloseoutArtifact): SafeClaim[] {
  const claims: SafeClaim[] = [];

  // Extract from issue
  if (artifact.issue) {
    claims.push(...extractClaimsFromIssue(artifact.issue));
  }

  // Extract from changes
  if (artifact.changes) {
    claims.push(...extractClaimsFromChanges(artifact.changes));
  }

  // Extract from invariants
  if (artifact.invariantsPreserved) {
    claims.push(...extractClaimsFromInvariants(artifact.invariantsPreserved));
  }

  // Extract from test results
  if (artifact.testResults) {
    claims.push(...extractClaimsFromTests(artifact.testResults));
  }

  // Extract from docs summary if present
  // Note: docsSummary is not part of CloseoutArtifact, it's a separate input

  // Deduplicate claims
  const uniqueClaims = new Map<string, SafeClaim>();
  for (const claim of claims) {
    const key = claim.claim;
    if (!uniqueClaims.has(key)) {
      uniqueClaims.set(key, claim);
    }
  }

  return Array.from(uniqueClaims.values());
}
